'use client';

import React, { useState, useEffect } from 'react';
import { Drawer } from 'vaul';
import apiConfig from '@/config/url-api';
import useAuthStore from '@/store/auth';
import {
  User,
  Lock,
  Palette,
  ChevronRight,
  LogOut,
  FileText,
  Shield,
  HelpCircle,
  ArrowLeft,
  X,
  Mail,
  Phone,
  Clock,
  ExternalLink,
  MapPin,
} from 'lucide-react';
import AccountTab from './AccountTab';
import CredentialsTab from './CredentialsTab';
import AppearanceTab from './AppearanceTab';

type Tab =
  | 'Account'
  | 'Credentials'
  | 'Appearance'
  | 'PrivacyPolicy'
  | 'TermsConditions'
  | 'HelpSupport'
  | null;

interface SettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const MENU_ITEMS = [
  {
    id: 'Account' as const,
    title: 'Account',
    subtitle: 'Customer name, street address, city, phone',
    icon: User,
    tint: '#0095f8',
    bgLight: '#e8f2ff',
    bgDark: 'rgba(0, 149, 248, 0.15)',
  },
  {
    id: 'Credentials' as const,
    title: 'Credentials',
    subtitle: 'Current password, new password',
    icon: Lock,
    tint: '#ef4444',
    bgLight: '#ffece8',
    bgDark: 'rgba(239, 68, 68, 0.15)',
  },
  {
    id: 'Appearance' as const,
    title: 'Appearance',
    subtitle: 'Theme, map type, auto refresh',
    icon: Palette,
    tint: '#8b5cf6',
    bgLight: '#f5f3ff',
    bgDark: 'rgba(139, 92, 246, 0.15)',
  },
];

const SUPPORT_ITEMS = [
  {
    id: 'TermsConditions' as const,
    title: 'Terms & Conditions',
    subtitle: 'Terms of use and service agreement',
    icon: FileText,
    tint: '#ea580c',
    bgLight: '#fff3e0',
    bgDark: 'rgba(234, 88, 12, 0.15)',
  },
  {
    id: 'PrivacyPolicy' as const,
    title: 'Privacy Policy',
    subtitle: 'How we collect and use your data',
    icon: Shield,
    tint: '#0095f8',
    bgLight: '#e8f2ff',
    bgDark: 'rgba(0, 149, 248, 0.15)',
  },
  {
    id: 'HelpSupport' as const,
    title: 'Help & Support',
    subtitle: 'FAQs and contact information',
    icon: HelpCircle,
    tint: '#059669',
    bgLight: '#e6f9ee',
    bgDark: 'rgba(5, 150, 105, 0.15)',
  },
];

const SUPPORT_CONTENT: Record<
  string,
  { title: string; subtitle: string; body: React.ReactNode }
> = {
  TermsConditions: {
    title: 'Terms & Conditions',
    subtitle: 'Terms of use and service agreement',
    body: (
      <div className="space-y-4 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
        <p>
          By using <strong>Missi Track</strong>, you agree to these service terms provided by{' '}
          <strong>PT Mitos International Solution</strong>.
        </p>

        <div className="p-3 rounded-xl bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 space-y-1.5">
          <p className="font-semibold text-slate-800 dark:text-white">Maritime Service Scope:</p>
          <ul className="list-disc pl-4 space-y-1 text-slate-600 dark:text-slate-300">
            <li>Vessel GPS & AIS telemetry tracking via satellite and GSM.</li>
            <li>Voyager route planning, waypoints, and arrival estimation.</li>
            <li>Geofence zone alerts, overspeed, and off-track alarm monitoring.</li>
            <li>Marine sensor analytics (Engine RPM, Hour Meter, Battery).</li>
          </ul>
        </div>

        <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 text-amber-800 dark:text-amber-300 text-[11px] leading-relaxed">
          <strong>Navigational Notice:</strong> Missi Track is a fleet management and advisory tool. It is not a primary navigation aid (ECDIS). The vessel Master remains solely responsible for safe navigation and COLREGS compliance.
        </div>

        <p>
          You are responsible for safeguarding your credentials. We reserve the right to suspend accounts engaged in unauthorized access or tampering with transponders.
        </p>

        <div className="pt-2">
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 font-semibold border border-blue-200 dark:border-blue-900 hover:bg-blue-100 transition-colors"
          >
            <span>Read Full Terms & Conditions</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    ),
  },
  PrivacyPolicy: {
    title: 'Privacy Policy',
    subtitle: 'How we collect and use your data',
    body: (
      <div className="space-y-4 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
        <p>
          At <strong>Missi Track</strong>, we prioritize the protection and security of your fleet telemetry, sensor data, and account information.
        </p>

        <div className="p-3 rounded-xl bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 space-y-1.5">
          <p className="font-semibold text-slate-800 dark:text-white">Data We Protect:</p>
          <ul className="list-disc pl-4 space-y-1 text-slate-600 dark:text-slate-300">
            <li>Live GPS positions (latitude, longitude, speed, heading).</li>
            <li>Vessel identifiers (MMSI, IMO, Call Sign, Vessel Name).</li>
            <li>Onboard sensor diagnostics (RPM, Hour Meter, Voltage).</li>
            <li>Customer profile and contact information.</li>
          </ul>
        </div>

        <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 text-emerald-800 dark:text-emerald-300 text-[11px] leading-relaxed">
          <strong>No Data Selling:</strong> We never sell, lease, or monetize your fleet coordinates or customer data to third-party advertisers. All telemetry is encrypted in transit and at rest.
        </div>

        <p>
          You may request access to telemetry logs or permanent account data deletion upon termination of service by contacting <a href="mailto:support@mitos.id" className="text-blue-500 underline">support@mitos.id</a>.
        </p>

        <div className="pt-2">
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 font-semibold border border-blue-200 dark:border-blue-900 hover:bg-blue-100 transition-colors"
          >
            <span>Read Full Privacy Policy</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    ),
  },
  HelpSupport: {
    title: 'Help & Support',
    subtitle: 'FAQs and contact information',
    body: (
      <div className="space-y-4 text-xs text-slate-600 dark:text-slate-300">
        <p className="leading-relaxed">
          For technical assistance, account inquiries, or questions about vessel tracking, please
          contact our support team:
        </p>
        <div className="space-y-2.5 pt-1">
          <a
            href="mailto:support@missi-track.com"
            className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-slate-800 dark:text-slate-200 hover:text-blue-600 transition-colors"
          >
            <div className="h-8 w-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-500 shrink-0">
              <Mail className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[11px] text-slate-400">Email Support</p>
              <p className="text-xs font-semibold">support@mitos.id</p>
            </div>
          </a>

          <a
            href="tel:+622127874648"
            className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-slate-800 dark:text-slate-200 hover:text-blue-600 transition-colors"
          >
            <div className="h-8 w-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-500 shrink-0">
              <Phone className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[11px] text-slate-400">Phone</p>
              <p className="text-xs font-semibold">021 2787 4648</p>
            </div>
          </a>

          <div className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-slate-800 dark:text-slate-200">
            <div className="h-8 w-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-500 shrink-0 mt-0.5">
              <MapPin className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[11px] text-slate-400">Office Address</p>
              <p className="text-xs leading-relaxed font-medium text-slate-700 dark:text-slate-300">
                Perkantoran Tanjung Mas Raya, blok B1 nomor 44, RT.002/RW.001. Tanjung Barat, Jagakarsa, Jakarta Selatan.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 text-slate-800 dark:text-slate-200">
            <div className="h-8 w-8 rounded-lg bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center text-orange-500 shrink-0">
              <Clock className="w-4 h-4" />
            </div>
            <div>
              <p className="text-[11px] text-slate-400">Office Hours</p>
              <p className="text-xs font-semibold">Mon - Fri, 08:30 - 17:30 (GMT+7)</p>
            </div>
          </div>
        </div>
      </div>
    ),
  },
};

export default function SettingsMobileMenu({ isOpen, onClose }: SettingsDrawerProps) {
  const [activeTab, setActiveTab] = useState<Tab>(null);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab(null);
    }
  }, [isOpen]);

  const handleLogout = () => {
    localStorage.removeItem(apiConfig.auth.storageTokenKeyName);
    localStorage.removeItem(apiConfig.auth.storageUserKeyName);
    window.location.href = '/';
  };

  const getActiveTitle = () => {
    if (!activeTab) return { title: 'Settings', subtitle: 'Manage your preferences' };
    if (activeTab === 'Account') return { title: 'Account', subtitle: 'Settings detail' };
    if (activeTab === 'Credentials') return { title: 'Credentials', subtitle: 'Settings detail' };
    if (activeTab === 'Appearance') return { title: 'Appearance', subtitle: 'Settings detail' };
    return {
      title: SUPPORT_CONTENT[activeTab]?.title || 'Support',
      subtitle: SUPPORT_CONTENT[activeTab]?.subtitle || 'Information',
    };
  };

  if (!isOpen) return null;

  const headerInfo = getActiveTitle();

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          setActiveTab(null);
          onClose();
        }
      }}
      modal={false}
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/40 dark:bg-black/60 z-[9990]" />
        <Drawer.Content className="fixed bottom-0 left-0 right-0 top-0 z-[9999] flex h-full max-h-[100dvh] flex-col bg-slate-50 dark:bg-gray-900 dark:text-white shadow-2xl focus:outline-none">
          {/* Drawer Handle */}
          <div className="pt-2.5 pb-1 bg-white dark:bg-gray-900 shrink-0">
            <div className="mx-auto h-1.5 w-12 rounded-full bg-slate-300 dark:bg-gray-700" />
          </div>

          {/* Sticky Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-gray-800 bg-white dark:bg-gray-900 shrink-0">
            <div className="flex items-center gap-3">
              {activeTab && (
                <button
                  type="button"
                  onClick={() => setActiveTab(null)}
                  className="p-1.5 -ml-1 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div>
                <h2 className="text-base font-bold text-[#021c32] dark:text-white">
                  {headerInfo.title}
                </h2>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  {headerInfo.subtitle}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === null ? (
              <div className="p-5 pb-24 space-y-6">
                {/* User Info Header Card */}
                {user && (
                  <div className="flex items-center gap-3.5 p-4 rounded-2xl bg-white dark:bg-gray-800 border border-slate-100 dark:border-gray-700/60 shadow-sm">
                    <div className="h-12 w-12 rounded-2xl bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold text-lg shrink-0">
                      {user.customer_name ? user.customer_name.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-sm text-[#021c32] dark:text-white truncate">
                        {user.customer_name || 'User'}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        {user.email || ''}
                      </p>
                    </div>
                  </div>
                )}

                {/* Primary Settings Menu */}
                <div className="space-y-3">
                  {MENU_ITEMS.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setActiveTab(item.id)}
                        className="w-full flex items-center rounded-2xl bg-white dark:bg-gray-800 p-4 border border-slate-100 dark:border-gray-700/60 shadow-sm hover:bg-slate-50/80 dark:hover:bg-gray-750 active:scale-[0.99] transition-all text-left"
                      >
                        <div
                          className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0"
                          style={{ backgroundColor: item.bgLight }}
                        >
                          <Icon className="w-6 h-6" style={{ color: item.tint }} />
                        </div>
                        <div className="flex-1 ml-4 mr-2 min-w-0">
                          <h4 className="text-sm font-bold text-[#021c32] dark:text-white">
                            {item.title}
                          </h4>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed truncate">
                            {item.subtitle}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
                      </button>
                    );
                  })}
                </div>

                {/* Support Section */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-3 px-1">
                    Support
                  </h3>
                  <div className="space-y-3">
                    {SUPPORT_ITEMS.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setActiveTab(item.id)}
                          className="w-full flex items-center rounded-2xl bg-white dark:bg-gray-800 p-4 border border-slate-100 dark:border-gray-700/60 shadow-sm hover:bg-slate-50/80 dark:hover:bg-gray-750 active:scale-[0.99] transition-all text-left"
                        >
                          <div
                            className="h-12 w-12 rounded-2xl flex items-center justify-center shrink-0"
                            style={{ backgroundColor: item.bgLight }}
                          >
                            <Icon className="w-6 h-6" style={{ color: item.tint }} />
                          </div>
                          <div className="flex-1 ml-4 mr-2 min-w-0">
                            <h4 className="text-sm font-bold text-[#021c32] dark:text-white">
                              {item.title}
                            </h4>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed truncate">
                              {item.subtitle}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Logout Button */}
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 rounded-2xl bg-white dark:bg-gray-800 py-3.5 border border-slate-200 dark:border-gray-700 shadow-sm hover:bg-red-50 dark:hover:bg-red-950/20 active:scale-[0.99] transition-all"
                  >
                    <LogOut className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-bold text-red-500">Logout</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-900 min-h-full">
                {activeTab === 'Account' && <AccountTab />}
                {activeTab === 'Credentials' && <CredentialsTab />}
                {activeTab === 'Appearance' && <AppearanceTab />}
                {(activeTab === 'TermsConditions' ||
                  activeTab === 'PrivacyPolicy' ||
                  activeTab === 'HelpSupport') && (
                  <div className="p-5 pb-24 space-y-4">
                    <div>
                      <h3 className="text-lg font-bold text-[#021c32] dark:text-white">
                        {SUPPORT_CONTENT[activeTab]?.title}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {SUPPORT_CONTENT[activeTab]?.subtitle}
                      </p>
                    </div>
                    <div className="pt-2">{SUPPORT_CONTENT[activeTab]?.body}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
